import React from 'react';
import { Box, Typography, Chip, Button, IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, AttachFile as AttachFileIcon, Image as ImageIcon, PictureAsPdf as PdfIcon, Description as WordIcon, TableChart as ExcelIcon, Email as MsgIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';
import { getUserInitials, getUserDisplayName } from '../../../utils/queryDisplayUtils';
import { isImageFile, isPdfFile, isWordFile, isExcelFile, isMsgFile, getFileName } from '../../../utils/fileUtils';
import { formatDateTime } from '../../../utils/dateUtils';

/**
 * ConversationThread Component
 * Renders conversation thread messages and server-side drafts
 * 
 * @param {Array} sortedThread - Sorted thread messages (non-draft)
 * @param {Array} savedDrafts - Server-side draft entries from query.thread
 * @param {Object} user - Current user
 * @param {function} handleAttachmentClick - Attachment click handler
 * @param {function} handleSendDraftResponse - Send draft handler
 * @param {function} handleDeleteDraft - Delete draft handler (receives threadId)
 * @param {function} handleLoadDraft - Load draft handler
 * @param {boolean} isSubmittingResponse - Whether response is being submitted
 * @param {boolean} isSavingDraft - Whether draft is being saved
 * @param {boolean} isSendingDraft - Whether a draft is being sent
 */
const ConversationThread = ({
  sortedThread,
  savedDrafts,
  user,
  handleAttachmentClick,
  handleSendDraftResponse,
  handleDeleteDraft,
  handleLoadDraft,
  isSubmittingResponse,
  isSavingDraft,
  isSendingDraft,
}) => {
  // Combine submitted messages with server-side drafts
  const allMessages = [
    ...sortedThread.map(msg => ({ ...msg, isDraft: false })),
    ...savedDrafts.map(draft => ({ ...draft, isDraft: true }))
  ].sort((a, b) => {
    const aTime = a.sentAt ? new Date(a.sentAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
    const bTime = b.sentAt ? new Date(b.sentAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    if (aTime !== bTime) return bTime - aTime;
    // If same time, drafts come after submitted messages
    return a.isDraft ? 1 : -1;
  });

  if (allMessages.length === 0) {
    return (
      <Box
        sx={{
          p: spacing.sm,
          borderRadius: borderRadius.input,
          border: `1px dashed ${colors.borderDefault}`,
          background: 'linear-gradient(135deg, rgba(14, 23, 42, 0.05) 0%, rgba(250, 204, 21, 0.08) 100%)',
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: fonts.fontSizeSm,
        }}
      >
        No responses yet. Start the conversation by sending a response.
      </Box>
    );
  }

  const currentUserDisplayName = getUserDisplayName(user);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.sm,
      }}
    >
      {allMessages.map((message, index) => {
        const isLast = index === allMessages.length - 1;
        const isDraft = message.isDraft;
        const senderName = isDraft
          ? currentUserDisplayName
          : (message.sentBy?.full_name || message.sentBy?.name || 'Unknown');
        
        return (
          <Box key={isDraft ? `draft-${message._id}` : (message._id || index)} sx={{ display: 'flex', gap: spacing.sm }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '48px',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: isDraft 
                    ? 'linear-gradient(135deg, #ffa726 0%, #ffb74d 100%)'
                    : colors.primaryGradient,
                  color: colors.textWhite,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: fonts.weightSemiBold,
                  fontSize: fonts.fontSizeSm,
                  boxShadow: isDraft 
                    ? '0 8px 18px rgba(255, 167, 38, 0.3)'
                    : `0 12px 28px ${colors.primaryShadowHover}`,
                  position: 'relative',
                }}
              >
                {isDraft ? 'D' : getUserInitials(senderName)}
                {isDraft && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: colors.warning,
                      border: `2px solid ${colors.backgroundWhite}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <EditIcon sx={{ fontSize: '0.6rem', color: colors.textWhite }} />
                  </Box>
                )}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    flex: 1,
                    width: '2px',
                    background: `linear-gradient(180deg, ${colors.primaryTint} 0%, transparent 100%)`,
                    mt: spacing.xs,
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                p: spacing.sm,
                background: isDraft
                  ? 'linear-gradient(135deg, #fff8e1 0%, #ffe0b2 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: borderRadius.md,
                border: isDraft
                  ? `2px dashed ${colors.warning}`
                  : `1px solid ${colors.borderDefault}`,
                boxShadow: isDraft
                  ? '0 10px 24px rgba(255, 152, 0, 0.15)'
                  : '0 10px 24px rgba(14, 23, 42, 0.08)',
                position: 'relative',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: spacing.xs }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                    <Typography
                      variant="body2"
                      sx={{ 
                        fontWeight: fonts.weightSemiBold, 
                        fontSize: fonts.fontSizeSm,
                        mb: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      {senderName}
                    </Typography>
                    {isDraft && (
                      <Chip
                        label="Draft"
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: fonts.fontSizeXs,
                          backgroundColor: colors.warning + '20',
                          color: colors.warning,
                          fontWeight: fonts.weightSemiBold,
                          '& .MuiChip-label': {
                            padding: '0 6px',
                          },
                        }}
                      />
                    )}
                  </Box>
                  {!isDraft && message.sentBy?.designation && (
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: colors.textSecondary, 
                        fontSize: fonts.fontSizeXs,
                        mt: 0,
                        lineHeight: 1.2,
                        display: 'block',
                      }}
                    >
                      {message.sentBy.designation}
                    </Typography>
                  )}
                  {isDraft && user?.designation && (
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: colors.textSecondary, 
                        fontSize: fonts.fontSizeXs,
                        mt: 0,
                        lineHeight: 1.2,
                        display: 'block',
                      }}
                    >
                      {user.designation}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.textSecondary, fontSize: fonts.fontSizeXs }}
                >
                  {isDraft ? formatDateTime(message.sentAt || message.createdAt) : formatDateTime(message.sentAt)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: colors.textPrimaryDark, fontSize: fonts.fontSizeMd, whiteSpace: 'pre-wrap', mb: isDraft ? spacing.xs : 0 }}
              >
                {isDraft ? message.message : message.message}
              </Typography>

              {isDraft && message.attachments && message.attachments.length > 0 && (
                <Box sx={{ mt: spacing.xs, mb: spacing.xs }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      display: 'block',
                      mb: spacing.xs,
                      fontWeight: fonts.weightSemiBold,
                    }}
                  >
                    Attachments ({message.attachments.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                    {message.attachments.map((file, fileIndex) => (
                      <Button
                        key={file._id || fileIndex}
                        onClick={() => handleAttachmentClick(file)}
                        variant="outlined"
                        size="small"
                        startIcon={
                          isImageFile(file) ? (
                            <ImageIcon sx={{ fontSize: '1rem' }} />
                          ) : isPdfFile(file) ? (
                            <PdfIcon sx={{ fontSize: '1rem' }} />
                          ) : isWordFile(file) ? (
                            <WordIcon sx={{ fontSize: '1rem' }} />
                          ) : isExcelFile(file) ? (
                            <ExcelIcon sx={{ fontSize: '1rem' }} />
                          ) : isMsgFile(file) ? (
                            <MsgIcon sx={{ fontSize: '1rem' }} />
                          ) : (
                            <AttachFileIcon sx={{ fontSize: '1rem' }} />
                          )
                        }
                        sx={{
                          textTransform: 'none',
                          borderRadius: borderRadius.button,
                          fontSize: fonts.fontSizeXs,
                          padding: `${spacing.xs} ${spacing.sm}`,
                          cursor: 'pointer',
                          borderColor: colors.warning,
                          color: colors.textPrimary,
                          '&:hover': {
                            backgroundColor: colors.primaryTint,
                            borderColor: colors.primaryDark,
                          },
                        }}
                      >
                        {getFileName(file)}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {!isDraft && message.attachments && message.attachments.length > 0 && (
                <Box sx={{ mt: spacing.sm }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      display: 'block',
                      mb: spacing.xs,
                      fontWeight: fonts.weightSemiBold,
                    }}
                  >
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                    {message.attachments.map((file, fileIndex) => (
                      <Button
                        key={file._id || fileIndex}
                        onClick={() => handleAttachmentClick(file)}
                        variant="outlined"
                        size="small"
                        startIcon={
                          isImageFile(file) ? (
                            <ImageIcon sx={{ fontSize: '1rem' }} />
                          ) : isPdfFile(file) ? (
                            <PdfIcon sx={{ fontSize: '1rem' }} />
                          ) : isWordFile(file) ? (
                            <WordIcon sx={{ fontSize: '1rem' }} />
                          ) : isExcelFile(file) ? (
                            <ExcelIcon sx={{ fontSize: '1rem' }} />
                          ) : isMsgFile(file) ? (
                            <MsgIcon sx={{ fontSize: '1rem' }} />
                          ) : (
                            <AttachFileIcon sx={{ fontSize: '1rem' }} />
                          )
                        }
                        sx={{
                          textTransform: 'none',
                          borderRadius: borderRadius.button,
                          fontSize: fonts.fontSizeXs,
                          padding: `${spacing.xs} ${spacing.sm}`,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: colors.primaryTint,
                            borderColor: colors.primaryDark,
                          },
                        }}
                      >
                        {getFileName(file)}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {isDraft && (
                <Box sx={{ display: 'flex', gap: spacing.xs, mt: spacing.sm, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => handleSendDraftResponse(message)}
                    variant="contained"
                    size="small"
                    disabled={isSubmittingResponse || isSavingDraft || isSendingDraft}
                    sx={{
                      textTransform: 'none',
                      fontSize: fonts.fontSizeXs,
                      padding: `${spacing.xs} ${spacing.sm}`,
                      background: colors.primaryGradient,
                      color: colors.textWhite,
                      '&:hover': {
                        background: colors.primaryGradientHover,
                      },
                      '&:disabled': {
                        backgroundColor: colors.borderLight,
                        color: colors.textSecondary,
                      },
                    }}
                  >
                    {isSendingDraft ? 'Sending...' : 'Send Response'}
                  </Button>
                  <IconButton
                    onClick={() => handleDeleteDraft(message._id)}
                    size="small"
                    disabled={isSubmittingResponse || isSavingDraft || isSendingDraft}
                    sx={{
                      color: colors.error,
                      padding: spacing.xs,
                      '&:hover': {
                        backgroundColor: `${colors.error}10`,
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ConversationThread;
